import type { AutoDispatchOverviewResponse } from '@api/useAutoDispatch/response'
import { orderColumnDefs } from '@components/OrderDispatch/OptimizerWizard/columnDefs/orderColumnDefs'
import { GraviGrid } from '@gravitate-js/excalibrr'
import { useCallback, useMemo } from 'react'
import { useLockedOrders } from '@hooks/useLockedOrders'
import type { OrderGridProps } from '../types'
import type { ColDef, IRowNode, RowDataUpdatedEvent } from 'ag-grid-community'
import { useUserSettings } from '@hooks/settings'
import type { GridConfigState } from '@gravitate-js/excalibrr/dist/components/GraviGrid/hooks/useAGGridEvent'
import type { AgGridReactProps } from 'ag-grid-react'
import { gridViewManager } from '@/utils/grid'

const GRID_STORAGE_KEY = 'optimizer-wizard-order-grid'

export function OrderGrid({ orderData, handleOrderSelectionChanged, loading, selectedRun }: OrderGridProps) {
  const { lockedOrders } = useLockedOrders()
  const { updateSetting } = useUserSettings()
  const unavailableOrders = useMemo(
    () => lockedOrders?.filter((order) => order?.run_id !== selectedRun.id).map((o) => o.order_id),
    [lockedOrders, selectedRun]
  )
  const getSelectedOrderIds = useCallback(
    (runId: string) => lockedOrders?.filter((order) => order?.run_id === runId).map((o) => o.order_id),
    [lockedOrders, selectedRun]
  )

  const isRowSelectable = useCallback(
    (row: IRowNode) => row.selected || !unavailableOrders?.includes(row?.id),
    [unavailableOrders]
  )
  const getRowId = useCallback((row: { data: AutoDispatchOverviewResponse }) => row.data?.id, [])
  const agPropOverrides: AgGridReactProps = {
    getRowId,
    rowSelection: 'multiple' as const,
    suppressRowClickSelection: true,
    isRowSelectable,
  }
  const columnDefs: ColDef[] = useMemo(() => orderColumnDefs(true, lockedOrders), [lockedOrders, selectedRun])

  return (
    <div style={{ width: '100%' }}>
      <GraviGrid
        rowData={orderData || []}
        loading={loading}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        controlBarProps={{
          title: 'Orders',
        }}
        storageKey={GRID_STORAGE_KEY}
        onGridConfigChanged={(config: GridConfigState) => updateSetting(`gridConfig::${GRID_STORAGE_KEY}`, config)}
        onSelectionChanged={(params) => {
          if (params.source === 'api' || params.source === 'rowDataChanged') return
          handleOrderSelectionChanged(params.api.getSelectedRows())
        }}
        onRowDataUpdated={(params: RowDataUpdatedEvent) => {
          if (selectedRun?.id) {
            const selectedIds = getSelectedOrderIds(selectedRun.id)
            params.api.forEachNodeAfterFilterAndSort((row) => {
              row.setSelected(selectedIds?.includes(row.id))
              row.setRowSelectable(!unavailableOrders?.includes(row.id))
            })
          }
        }}
        {...gridViewManager(GRID_STORAGE_KEY, updateSetting)}
      />
    </div>
  )
}
